<template>
  <v-container fluid class="narcis-playground">
    <v-row>
      <!-- Left Panel: Code & Schema Editor -->
      <v-col cols="12" md="6" class="editor-panel">
        <v-card elevation="2">
          <v-card-title class="primary white--text">
            <v-icon left dark>mdi-code-braces</v-icon>
            Component Configuration
          </v-card-title>
          <v-card-text>
            <!-- Template Code Editor -->
            <v-expansion-panels v-model="openPanels" multiple>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon left>mdi-file-code</v-icon>
                    <strong>Template Code</strong>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-textarea
                    v-model="code"
                    label="Vue Template Code"
                    outlined
                    rows="20"
                    class="code-editor"
                    hint="Edit the Vue template code here"
                    persistent-hint
                  ></v-textarea>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Schema Editor -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon left>mdi-file-document-outline</v-icon>
                    <strong>JSON Schema</strong>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-textarea
                    v-model="schemaText"
                    label="JSON Schema"
                    outlined
                    rows="15"
                    class="code-editor"
                    hint="Edit the JSON schema here"
                    persistent-hint
                    :error-messages="schemaError"
                  ></v-textarea>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Initial Data Editor -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon left>mdi-database</v-icon>
                    <strong>Initial Data</strong>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-textarea
                    v-model="initialDataText"
                    label="Initial Data (JSON)"
                    outlined
                    rows="10"
                    class="code-editor"
                    hint="Edit the initial data here"
                    persistent-hint
                    :error-messages="dataError"
                  ></v-textarea>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Component Options Editor (NEW) -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div>
                    <v-icon left>mdi-function</v-icon>
                    <strong>Component Options (Methods & Computed)</strong>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-textarea
                    v-model="optionsText"
                    label="Component Options (JavaScript Object)"
                    outlined
                    rows="20"
                    class="code-editor"
                    hint="Define methods, computed properties, and data functions"
                    persistent-hint
                    :error-messages="optionsError"
                  ></v-textarea>
                  <v-alert type="info" dense text class="mt-2">
                    <small>
                      Example:<br>
                      <code>
                        {<br>
                        &nbsp;&nbsp;data: () => ({ counter: 0 }),<br>
                        &nbsp;&nbsp;computed: {<br>
                        &nbsp;&nbsp;&nbsp;&nbsp;doubleCounter() { return this.counter * 2 }<br>
                        &nbsp;&nbsp;},<br>
                        &nbsp;&nbsp;methods: {<br>
                        &nbsp;&nbsp;&nbsp;&nbsp;increment() { this.counter++ }<br>
                        &nbsp;&nbsp;}<br>
                        }
                      </code>
                    </small>
                  </v-alert>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Action Buttons -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-btn
                  color="success"
                  large
                  block
                  @click="applyChanges"
                  :disabled="hasErrors"
                >
                  <v-icon left>mdi-play</v-icon>
                  Apply Changes
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn
                  color="warning"
                  block
                  @click="resetToDefaults"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Reset
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn
                  color="info"
                  block
                  @click="copyCurrentConfig"
                >
                  <v-icon left>mdi-content-copy</v-icon>
                  Copy Config
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Panel: Preview & Data Output -->
      <v-col cols="12" md="6" class="preview-panel">
        <v-card elevation="2" class="mb-4">
          <v-card-title class="secondary white--text">
            <v-icon left dark>mdi-eye</v-icon>
            Live Preview
          </v-card-title>
          <v-card-text class="preview-container">
            <div v-if="mounted && compiledCode && payload" class="preview-content">
              <DynamicComponentRenderer
                v-model="payload"
                :code="compiledCode"
                :schema="compiledSchema"
                :options="compiledOptions"
                @run_action="handleAction"
              />
            </div>
            <div v-else class="text-center pa-8">
              <v-icon size="64" color="grey">mdi-eye-off</v-icon>
              <p class="grey--text mt-2">Click "Apply Changes" to see preview</p>
            </div>
          </v-card-text>
        </v-card>

        <v-card elevation="2">
          <v-card-title class="info white--text">
            <v-icon left dark>mdi-code-json</v-icon>
            Current Data Output
          </v-card-title>
          <v-card-text>
            <v-textarea
              :value="payloadDisplay"
              label="Current Payload (Read-only)"
              outlined
              rows="15"
              readonly
              class="code-editor"
            ></v-textarea>
            <v-alert
              v-if="lastAction"
              type="success"
              dense
              text
              class="mt-2"
            >
              Last action executed: <strong>{{ lastAction }}</strong>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { fill_instance_from_schema } from "@/modules/entities/domain/GetTreeFromSchema";
import DynamicComponentRenderer from "@/modules/entities/infra/DynamicComponentRenderer.vue";

export default {
  name: "NarcisPlayground",
  components: {
    DynamicComponentRenderer
  },
  data() {
    return {
      mounted: false,
      payload: {},
      compiledCode: null,
      compiledSchema: null,
      compiledOptions: null,
      openPanels: [0], // First panel open by default
      schemaError: null,
      dataError: null,
      optionsError: null,
      lastAction: null,

      // Editable text versions
      code: `<v-card color="blue-grey lighten-5" elevation="3">
  <v-card-text>
    <v-row>
      <v-col cols="12">
        <v-card outlined style="margin-bottom: 16px;" color="indigo lighten-5" elevation="2">
          <v-card-title style="font-size: 16px; background: linear-gradient(45deg, #3f51b5, #9c27b0); color: white; border-radius: 4px 4px 0 0;">DNI</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localData.dni.numero"
                  label="Número"
                  @input="update"
                  color="indigo"
                  required
                  outlined
                  background-color="white"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localData.dni.fecha"
                  label="Fecha"
                  type="date"
                  @input="update"
                  color="indigo"
                  required
                  outlined
                  background-color="white"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="localData.nombre"
          label="Nombre"
          @input="update"
          color="teal"
          required
          outlined
          background-color="teal lighten-5"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="localData.codigo"
          label="Código"
          @input="update"
          color="orange"
          required
          outlined
          background-color="orange lighten-5"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="localData.cuenta_e"
          label="Cuenta"
          @input="update"
          color="purple"
          required
          outlined
          background-color="purple lighten-5"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="localData.fecha_inc"
          label="Fecha incorporación"
          type="date"
          @input="update"
          color="green"
          required
          outlined
          background-color="green lighten-5"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-checkbox
          v-model="localData.activo"
          label="Activo"
          @change="update"
          color="success"
          required
          background-color="success lighten-5"
        ></v-checkbox>
      </v-col>
      <v-col cols="12" md="6">
        <v-btn @click="run_action('actionman')" color="primary">
          ACTIONMAN
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="localData.Biografia"
          label="Biografía"
          @input="update"
          color="blue"
          required
          outlined
          background-color="blue lighten-5"
        ></v-textarea>
      </v-col>
    </v-row>
  </v-card-text>
</v-card>`,

      schemaText: '',
      initialDataText: '{}',
      optionsText: '',

      defaultSchema: {
        "type": "object",
        "title": "",
        "required": [
          "nombre",
          "codigo",
          "cuenta_e",
          "fecha_inc",
          "activo",
          "Biografia",
          "dni"
        ],
        "properties": {
          "dni": {
            "type": "object",
            "title": "",
            "required": [
              "numero",
              "fecha"
            ],
            "properties": {
              "fecha": {
                "type": "string",
                "title": ""
              },
              "numero": {
                "type": "string",
                "title": ""
              }
            }
          },
          "activo": {
            "type": "boolean",
            "title": "Activo"
          },
          "codigo": {
            "type": "string",
            "title": "Código"
          },
          "nombre": {
            "type": "string",
            "title": "Nombre"
          },
          "cuenta_e": {
            "type": "string",
            "title": "Cuenta"
          },
          "Biografia": {
            "type": "string",
            "title": ""
          },
          "fecha_inc": {
            "type": "string",
            "title": "Fecha incorporación"
          }
        }
      }
    }
  },
  computed: {
    payloadDisplay() {
      return JSON.stringify(this.payload, null, 2);
    },
    hasErrors() {
      return !!(this.schemaError || this.dataError || this.optionsError);
    }
  },
  mounted() {
    this.schemaText = JSON.stringify(this.defaultSchema, null, 2);
    this.applyChanges();
    this.mounted = true;
  },
  methods: {
    applyChanges() {
      // Validate and parse schema
      try {
        this.compiledSchema = JSON.parse(this.schemaText);
        this.schemaError = null;
      } catch (e) {
        this.schemaError = `Invalid JSON Schema: ${e.message}`;
        return;
      }

      // Validate and parse component options
      if (this.optionsText.trim()) {
        try {
          // Test if the options string is valid JavaScript
          const testFn = new Function('return ' + this.optionsText)
          testFn() // Execute to check for syntax errors
          this.compiledOptions = this.optionsText;
          this.optionsError = null;
        } catch (e) {
          this.optionsError = `Invalid JavaScript Options: ${e.message}`;
          return;
        }
      } else {
        this.compiledOptions = null;
        this.optionsError = null;
      }

      // Validate and parse initial data
      try {
        const initialData = JSON.parse(this.initialDataText);
        this.dataError = null;

        // Fill payload from schema
        this.payload = fill_instance_from_schema(initialData, this.compiledSchema);

        // Compile code
        this.compiledCode = this.code;

        this.$nextTick(() => {
          console.log('Changes applied successfully');
        });
      } catch (e) {
        this.dataError = `Invalid JSON Data: ${e.message}`;
      }
    },

    resetToDefaults() {
      this.schemaText = JSON.stringify(this.defaultSchema, null, 2);
      this.initialDataText = '{}';
      this.optionsText = '';
      this.code = this.$options.data().code;
      this.applyChanges();
    },

    copyCurrentConfig() {
      const config = {
        code: this.code,
        schema: JSON.parse(this.schemaText),
        data: this.payload,
        options: this.optionsText || null
      };

      navigator.clipboard.writeText(JSON.stringify(config, null, 2))
        .then(() => {
          alert('Configuration copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy:', err);
        });
    },

    handleAction(action_code) {
      console.log('Action executed:', action_code);
      this.lastAction = action_code;
    }
  }
}
</script>

<style scoped>
.narcis-playground {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 20px;
}

.editor-panel,
.preview-panel {
  height: 100%;
}

.code-editor {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.code-editor >>> textarea {
  font-family: 'Courier New', monospace;
  line-height: 1.5;
}

.preview-container {
  background-color: #fafafa;
  min-height: 400px;
  padding: 16px;
}

.preview-content {
  background-color: white;
  padding: 8px;
  border-radius: 4px;
}
</style>
